import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ReactNode, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { AddOnTextInput, Badge, Button, DropdownSelect, Icon, Loading, TextArea, Toggle, Tooltip, Typography } from "@/components/atoms";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useGetAdminSettingsQuery, useUpdateAdminSettingsMutation } from "@/redux/apis/clientAdminSettings/clientAdminSettingsApi";
import {
  AdminSettingsCapabilities,
  ClientAdminSettings,
  DispatchMode,
  UpdateAdminSettingsParams,
} from "@/redux/apis/clientAdminSettings/types";
import { addToast } from "@/utils";

const DISPATCH_OPTIONS: { value: DispatchMode; name: string }[] = [
  { value: "always", name: "Always dispatch as per operator rules" },
  { value: "always-no-public-network", name: "Always dispatch. Never dispatch to public network" },
  { value: "always-no-private-network", name: "Always dispatch. Never dispatch to partners OR public network" },
  { value: "never", name: "Never dispatch" },
];

const dispatchLabel = (value: DispatchMode) => DISPATCH_OPTIONS.find((o) => o.value === value)?.name ?? value;

const DISPATCH_TOOLTIP =
  "If disabled, drivers and passengers will see real phone numbers and operator will no longer be able to monitor communications. This applies for all bookings created under this profile";

interface FormData {
  vip_status: boolean;
  number_masked: boolean;
  dispatch_enabled: DispatchMode;
  dispatch_mins_before_pickup: string;
  operator_client_note: string;
}

const toFormData = (s: ClientAdminSettings): FormData => ({
  vip_status: s.vip_status,
  number_masked: s.number_masked,
  dispatch_enabled: s.dispatch_enabled,
  dispatch_mins_before_pickup: s.dispatch_mins_before_pickup == null ? "" : String(s.dispatch_mins_before_pickup),
  operator_client_note: s.operator_client_note ?? "",
});

const readValue = (text: string) => (
  <Typography variant="action" className="!m-0 text-neutral-black">
    {text}
  </Typography>
);

/**
 * Label + helper (+ optional info icon) on the left, value/control on the right;
 * two-column on a wide card, stacked when narrow. Bespoke rather than
 * ProfileDetail, which forces a divider and viewport (not container) breakpoints.
 */
const Field = ({ label, description, icon, children }: { label: string; description?: string; icon?: ReactNode; children: ReactNode }) => (
  <div className="flex flex-col gap-2 @[560px]:flex-row @[560px]:gap-16">
    <div className="flex shrink-0 flex-col gap-1 @[560px]:w-[200px]">
      <div className="flex items-center gap-2">
        <Typography variant="action" className="!m-0 text-neutral-dark-gray">
          {label}
        </Typography>
        {icon}
      </div>
      {description && (
        <Typography variant="paragraph" className="!m-0 text-neutral-dark-gray">
          {description}
        </Typography>
      )}
    </div>
    <div className="flex min-w-0 flex-1 items-start">{children}</div>
  </div>
);

/**
 * Operator-only client Admin Settings (read + edit).
 *
 * Operator-only: only mounted under the operator's ManageClient.Settings and
 * enforced server-side (the client's own token gets 403). Fields whose domain
 * capability is off are hidden, mirroring the legacy admin tab's flag gating.
 */
export const AdminSettings = () => {
  const { accountId } = useClientScope();
  const { data, isLoading, error, refetch } = useGetAdminSettingsQuery({ accountId });
  const [updateAdminSettings, { isLoading: isSaving }] = useUpdateAdminSettingsMutation();
  const [isEditing, setIsEditing] = useState(false);

  const { control, handleSubmit, reset } = useForm<FormData>();

  // Hydrate the form from server data, but never while editing — a background
  // refetch (e.g. the post-save tag invalidation) must not clobber live edits.
  useEffect(() => {
    if (data && !isEditing) reset(toFormData(data));
  }, [data, isEditing, reset]);

  const title = (
    <Typography variant="title" className="mb-3 block text-neutral-black">
      Admin Settings
    </Typography>
  );

  if (isLoading) {
    return (
      <div>
        {title}
        <Loading className="relative min-h-[120px] !bg-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        {title}
        <div className="flex flex-col items-start gap-2 rounded-lg border border-neutral-gray bg-white p-5">
          <Typography variant="paragraph" className="text-danger">
            {error ? getErrorMessages(error)[0] : "Unable to load admin settings."}
          </Typography>
          <Button variant="secondary" size="sm" onClick={refetch}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const caps: AdminSettingsCapabilities = data.capabilities;

  const handleCancel = () => {
    reset(toFormData(data));
    setIsEditing(false);
  };

  const onSubmit = handleSubmit(async (form) => {
    const payload: UpdateAdminSettingsParams = { accountId };
    if (caps.vip_status) payload.vip_status = form.vip_status;
    if (caps.number_masked) payload.number_masked = form.number_masked;
    if (caps.dispatch_enabled) payload.dispatch_enabled = form.dispatch_enabled;
    if (caps.dispatch_mins_before_pickup) {
      payload.dispatch_mins_before_pickup = form.dispatch_mins_before_pickup === "" ? null : Number(form.dispatch_mins_before_pickup);
    }
    if (caps.operator_client_note) payload.operator_client_note = (form.operator_client_note ?? "").trim() || null;

    try {
      await updateAdminSettings(payload).unwrap();
      addToast("success", "Admin settings updated");
      setIsEditing(false);
    } catch (e) {
      getErrorMessages(e as FetchBaseQueryError).forEach((message) => addToast("danger", message));
    }
  });

  const dispatchInfoIcon = (
    <Tooltip content={DISPATCH_TOOLTIP} placement="top">
      <Icon name="InfoCircle" size="sm" className="cursor-help text-neutral-silver-gray" />
    </Tooltip>
  );

  return (
    <div>
      {title}

      <form noValidate onSubmit={onSubmit} className="flex flex-col rounded-lg border border-neutral-gray bg-white p-5 @container">
        <div className="flex items-start justify-between gap-4 pb-6">
          <Typography variant="title" className="text-neutral-silver-gray">
            Operator Only Settings
          </Typography>
          <div className="flex shrink-0 items-center gap-2">
            {isEditing ? (
              <div className="hidden items-center gap-2 @[560px]:flex">
                <Button variant="secondary" size="sm" type="button" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit" disabled={isSaving}>
                  Save
                </Button>
              </div>
            ) : (
              <Button variant="secondary" size="sm" type="button" endIcon="Edit2" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-7">
          {caps.vip_status && (
            <Field label="VIP Status" description="VIPs are highlighted in the dashboard">
              {isEditing ? (
                <Controller
                  control={control}
                  name="vip_status"
                  render={({ field }) => <Toggle size="sm" checked={field.value} onChange={field.onChange} />}
                />
              ) : (
                <Badge type={data.vip_status ? "success" : "disabled"} message={data.vip_status ? "Yes" : "No"} />
              )}
            </Field>
          )}

          {caps.number_masked && (
            <Field label="Number Masking Enabled" description="Hides phone numbers for privacy">
              {isEditing ? (
                <Controller
                  control={control}
                  name="number_masked"
                  render={({ field }) => <Toggle size="sm" checked={field.value} onChange={field.onChange} />}
                />
              ) : (
                <Badge type={data.number_masked ? "success" : "disabled"} message={data.number_masked ? "Enabled" : "Disabled"} />
              )}
            </Field>
          )}

          {caps.dispatch_enabled && (
            <Field label="Automatically dispatch jobs for this client?" icon={dispatchInfoIcon}>
              {isEditing ? (
                <Controller
                  control={control}
                  name="dispatch_enabled"
                  render={({ field }) => (
                    <DropdownSelect
                      className="w-full max-w-[320px]"
                      optionsClassName="w-max min-w-full max-w-[520px]"
                      options={DISPATCH_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              ) : (
                readValue(dispatchLabel(data.dispatch_enabled))
              )}
            </Field>
          )}

          {caps.dispatch_mins_before_pickup && (
            <Field label="Hold dispatch until minutes before pickup" description="Leave blank for no delay">
              {isEditing ? (
                <Controller
                  control={control}
                  name="dispatch_mins_before_pickup"
                  render={({ field }) => (
                    <AddOnTextInput
                      type="number"
                      min={0}
                      placeholder="Eg. 60"
                      endAddOn="Min"
                      className="w-[150px]"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              ) : (
                readValue(data.dispatch_mins_before_pickup == null ? "-" : String(data.dispatch_mins_before_pickup))
              )}
            </Field>
          )}

          {caps.operator_client_note && (
            <Field label="Notes for this Client" description="Visible only to the Operator & Admin">
              {isEditing ? (
                <Controller
                  control={control}
                  name="operator_client_note"
                  render={({ field }) => (
                    <div className="w-full max-w-[520px]">
                      <TextArea className="w-full" rows={3} maxLength={2000} value={field.value} onChange={field.onChange} />
                    </div>
                  )}
                />
              ) : (
                <Typography variant="action" className="!m-0 whitespace-pre-line text-neutral-black">
                  {data.operator_client_note || "-"}
                </Typography>
              )}
            </Field>
          )}
        </div>

        {isEditing && (
          <div className="mt-6 flex flex-col gap-2 @[560px]:hidden">
            <Button variant="primary" size="md" type="submit" className="w-full" disabled={isSaving}>
              Save
            </Button>
            <Button variant="secondary" size="md" type="button" className="w-full" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};
